/**
 * components/ExecutionWorkspace.tsx — MER Algo Footprints v7.1H
 *
 * Execution workspace for HF Home. Two columns:
 *   Left  — live price chart (CandleChart, same data path as PricePanel)
 *   Right — Execution Plan card (entry / invalidation / targets / R·R / actions)
 *
 * Price is permanently visible here. No "Open Price tab" placeholder.
 */

import React, { useState, useCallback } from 'react';
import { useStore }              from '../../../store';
import { getSymbolContext }      from '../../../selectors/symbolContext';
import { CandleChart } from '../../chart/CandleChart';
import { ChartTimeframeStrip } from '../../../components/trading/ChartTimeframeStrip';
import { calcVWAP }              from '../../../utils/calc';
import { HedgeFundModeViewModel } from '../types/hedgeFundViewModel';
import { SMCOverlaySettings } from '../../../types';

// ─── Execution Plan Card ──────────────────────────────────────────────────────

interface PlanCardProps {
  vm:         HedgeFundModeViewModel;
  onSaveSnap: () => Promise<void>;
  onPass:     () => void;
  snapSaving: boolean;
  snapSaved:  boolean;
}

// Color maps for decision fields
const BIAS_COLOR:  Record<string, string> = { bullish: '#2ec4a9', bearish: '#f0534e', neutral: '#64748b' };
const BIAS_LABEL:  Record<string, string> = { bullish: '▲ LONG', bearish: '▼ SHORT', neutral: '◆ NEUTRAL' };

// Quiet section divider
function SectionDivider({ label }: { label: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '6px 0 3px',
    }}>
      <span style={{
        fontSize: 8, fontWeight: 700, letterSpacing: 1.4,
        color: '#2e3548', fontFamily: 'var(--mono)',
        textTransform: 'uppercase', flexShrink: 0,
      }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 1, background: '#1e2638' }} />
    </div>
  );
}

// Value row: muted label left, strong value right
function PlanRow({ label, value, color, size, accent }: {
  label: string; value: string | null; color?: string; size?: number; accent?: boolean;
}) {
  if (!value) return null;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      padding: accent ? '5px 8px' : '4px 0',
      background: accent ? 'rgba(240,83,78,0.06)' : 'transparent',
      borderRadius: accent ? 3 : 0,
      borderLeft: accent ? '2px solid rgba(240,83,78,0.4)' : 'none',
      marginLeft: accent ? -8 : 0,
      marginRight: accent ? -8 : 0,  // compensated by padding
    }}>
      <span style={{
        fontSize: 9, color: '#2e3548', fontFamily: 'var(--mono)',
        letterSpacing: 0.5, flexShrink: 0,
      }}>
        {label}
      </span>
      <span style={{
        fontSize: size ?? 12, fontWeight: 700,
        color: color ?? '#9fa5b0',
        fontFamily: 'var(--mono)',
      }}>
        {value}
      </span>
    </div>
  );
}

// Target row — ladder presentation
function TargetRow({ label, value, color, isFirst }: {
  label: string; value: string | null; color: string; isFirst?: boolean;
}) {
  if (!value) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '4px 0',
      borderTop: isFirst ? 'none' : '1px solid #151d2a',
    }}>
      {/* Ladder indicator */}
      <div style={{
        width: 3, height: 24, borderRadius: 2,
        background: color, opacity: 0.7, flexShrink: 0,
      }} />
      <span style={{ fontSize: 9, color: '#2e3548', fontFamily: 'var(--mono)', minWidth: 20 }}>
        {label}
      </span>
      <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color, fontFamily: 'var(--mono)' }}>
        {value}
      </span>
    </div>
  );
}

// ─── State Transition Panel ───────────────────────────────────────────────────
// Renders the explicit market state, actionability, and triggers.
// Inserted above the legacy execution levels in ExecutionPlanCard.

function StateTransitionPanel({ st }: { st: HedgeFundModeViewModel['stateTransition'] }) {
  const actionColor =
    st.actionability === 'actionable' ? '#2ec4a9' :
    st.actionability === 'pending'    ? '#facc15' : '#64748b';

  return (
    <div style={{
      background: '#080d14',
      border: `1px solid ${st.stateColor}30`,
      borderLeft: `3px solid ${st.stateColor}`,
      borderRadius: 4,
      padding: '8px 10px',
      marginBottom: 10,
    }}>
      {/* State name + action badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{
          fontSize: 10, fontWeight: 800, color: st.stateColor,
          fontFamily: 'var(--mono)', letterSpacing: 0.8,
        }}>
          {st.state.replace(/_/g, ' ')}
        </span>
        <span style={{
          fontSize: 8, fontWeight: 700,
          color: actionColor,
          background: `${actionColor}15`,
          border: `1px solid ${actionColor}35`,
          borderRadius: 2, padding: '1px 6px', letterSpacing: 1,
          fontFamily: 'var(--mono)',
        }}>
          {st.actionLabel}
        </span>
      </div>

      {/* Operator guidance */}
      <div style={{ fontSize: 9.5, color: '#6b7280', lineHeight: 1.5, marginBottom: 6, fontFamily: 'var(--mono)' }}>
        {st.operatorGuidance}
      </div>

      {/* Quality / confidence mini-bars */}
      <div style={{ display: 'flex', gap: 12, marginBottom: st.longTrigger || st.shortTrigger ? 8 : 0 }}>
        {[
          { label: 'QUALITY', value: st.setupQuality, color: st.stateColor },
          { label: 'CONF',    value: st.confidence,   color: '#a78bfa' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ flex: 1 }}>
            <div style={{ fontSize: 7, color: '#2e3548', letterSpacing: 1, marginBottom: 2, fontFamily: 'var(--mono)' }}>{label}</div>
            <div style={{ height: 2, background: '#1a2236', borderRadius: 1, overflow: 'hidden' }}>
              <div style={{ width: `${value}%`, height: '100%', background: color, borderRadius: 1 }} />
            </div>
          </div>
        ))}
      </div>

      {/* Effective confidence — the usable result after governed provider
          evidence. Distinct from the CONF bar above, which reports structural
          strength. Read verbatim: the band is computed once, upstream. */}
      {st.effectiveConfidenceBand && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          marginTop: 6, marginBottom: st.longTrigger || st.shortTrigger ? 8 : 0,
          fontFamily: 'var(--mono)',
        }}>
          <span style={{ fontSize: 7, color: '#2e3548', letterSpacing: 1 }}>EFFECTIVE</span>
          <span style={{
            fontSize: 8, fontWeight: 700, letterSpacing: 0.6,
            color:
              st.effectiveConfidenceBand === 'high'   ? '#2ec4a9' :
              st.effectiveConfidenceBand === 'medium' ? '#facc15' : '#f0534e',
          }}>
            {st.effectiveConfidenceBand.toUpperCase()}
          </span>
        </div>
      )}

      {/* Triggers */}
      {st.longTrigger && (
        <div style={{ marginTop: 5, fontSize: 9, fontFamily: 'var(--mono)' }}>
          <span style={{ color: '#2e3548', letterSpacing: 0.5 }}>LONG ▶ </span>
          <span style={{ color: '#2ec4a9' }}>{st.longTrigger}</span>
        </div>
      )}
      {st.shortTrigger && (
        <div style={{ marginTop: 4, fontSize: 9, fontFamily: 'var(--mono)' }}>
          <span style={{ color: '#2e3548', letterSpacing: 0.5 }}>SHORT ▶ </span>
          <span style={{ color: '#f0534e' }}>{st.shortTrigger}</span>
        </div>
      )}

      {/* Context flags (compact) */}
      {st.contextFlags.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6,
        }}>
          {st.contextFlags.map((f, i) => (
            <span key={i} style={{
              fontSize: 7.5, color: '#3a4558',
              background: '#0d1117',
              border: '1px solid #1a2236',
              borderRadius: 2, padding: '1px 5px',
              fontFamily: 'var(--mono)', letterSpacing: 0.3,
            }}>{f}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export function ExecutionPlanCard({ vm, onSaveSnap, onPass, snapSaving, snapSaved }: PlanCardProps) {
  const { execution, decision } = vm;
  const hasData  = execution.entry || execution.invalidation || execution.target1;
  const biasColor = BIAS_COLOR[decision.bias] ?? '#64748b';

  return (
    <div style={{
      background: '#0d1117',
      border: '1px solid #1a2236',
      borderTop: `2px solid ${biasColor}`,
      borderRadius: 6,
      padding: '12px 14px',
      display: 'flex', flexDirection: 'column', gap: 0,
      flex: '1 1 auto', minHeight: 0,
      fontFamily: 'var(--mono)',
    }}>

      {/* ── State Transition Panel ─────────────────────────────────────── */}
      <StateTransitionPanel st={vm.stateTransition} />

      {/* ── Decision header — State Transition Layer ────────────────────── */}
      {(() => {
        const st = vm.stateTransition;
        const stColor = st.stateColor;
        return (
          <>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 6,
            }}>
              {/* Directional bias */}
              <span style={{
                fontSize: 15, fontWeight: 800, color: biasColor,
                letterSpacing: 0.5,
              }}>
                {BIAS_LABEL[decision.bias] ?? '◆ NEUTRAL'}
              </span>

              {/* Action label chip — now from state transition */}
              <span style={{
                fontSize: 9, fontWeight: 700, color: stColor,
                background: `${stColor}14`,
                border: `1px solid ${stColor}35`,
                borderRadius: 3, padding: '2px 8px', letterSpacing: 1,
              }}>
                {st.actionLabel}
              </span>
            </div>

            {/* State name — full readable label */}
            <div style={{
              fontSize: 9, fontWeight: 700, color: stColor,
              letterSpacing: 0.8, marginBottom: 4,
              textTransform: 'uppercase' as const,
            }}>
              {st.state.replace(/_/g, ' ')}
            </div>

            {/* Operator guidance */}
            <div style={{
              fontSize: 9, color: '#4a5568', lineHeight: 1.45, marginBottom: 8,
              paddingBottom: 8, borderBottom: '1px solid #151d2a',
            }}>
              {st.operatorGuidance}
            </div>

            {/* Triggers — long / short */}
            {(st.longTrigger || st.shortTrigger) && (
              <div style={{ marginBottom: 6 }}>
                <SectionDivider label="Triggers" />
                {st.longTrigger && (
                  <PlanRow label="▲ LONG" value={st.longTrigger} color="#2ec4a9" size={10} />
                )}
                {st.shortTrigger && (
                  <PlanRow label="▼ SHORT" value={st.shortTrigger} color="#f0534e" size={10} />
                )}
              </div>
            )}

            {/* Confirmation */}
            {st.confirmation.length > 0 && (
              <div style={{ marginBottom: 6 }}>
                <SectionDivider label="Confirmation" />
                {st.confirmation.map((c, i) => (
                  <div key={i} style={{ display: 'flex', gap: 5, padding: '2px 0' }}>
                    <span style={{ color: '#2ec4a9', fontSize: 8, flexShrink: 0, marginTop: 2 }}>✓</span>
                    <span style={{ fontSize: 9, color: '#4a5568', lineHeight: 1.4 }}>{c}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Failure / invalidation */}
            {st.failure.length > 0 && (
              <div style={{ marginBottom: 6 }}>
                <SectionDivider label="Failure" />
                {st.failure.map((f, i) => (
                  <div key={i} style={{ display: 'flex', gap: 5, padding: '2px 0' }}>
                    <span style={{ color: '#f0534e', fontSize: 8, flexShrink: 0, marginTop: 2 }}>✕</span>
                    <span style={{ fontSize: 9, color: '#4a5568', lineHeight: 1.4 }}>{f}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Context flags */}
            {st.contextFlags.length > 0 && (
              <div style={{
                display: 'flex', flexWrap: 'wrap' as const, gap: 4,
                marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #151d2a',
              }}>
                {st.contextFlags.map((flag, i) => (
                  <span key={i} style={{
                    fontSize: 8, color: '#4a5568',
                    background: '#111827', border: '1px solid #1a2236',
                    borderRadius: 2, padding: '2px 5px',
                  }}>
                    {flag}
                  </span>
                ))}
              </div>
            )}

            {/* Quality bars */}
            <div style={{
              display: 'flex', gap: 12, marginBottom: 8,
              paddingBottom: 8, borderBottom: '1px solid #151d2a',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 8, color: '#2e3548', marginBottom: 3, letterSpacing: 1 }}>QUALITY</div>
                <div style={{ height: 3, background: '#1a2236', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ width: `${st.setupQuality}%`, height: '100%', background: stColor, borderRadius: 2 }} />
                </div>
                <div style={{ fontSize: 10, fontWeight: 700, color: stColor, marginTop: 2 }}>{st.setupQuality}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 8, color: '#2e3548', marginBottom: 3, letterSpacing: 1 }}>CONF</div>
                <div style={{ height: 3, background: '#1a2236', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ width: `${st.confidence}%`, height: '100%', background: 'var(--gold)', borderRadius: 2 }} />
                </div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold)', marginTop: 2 }}>{st.confidence}</div>
              </div>
            </div>
          </>
        );
      })()}

      {/* Setup name */}
      {decision.setupName && (
        <div style={{ fontSize: 9, color: '#3a4558', marginBottom: 8, letterSpacing: 0.3 }}>
          {decision.setupName}
        </div>
      )}



      {/* ── Execution levels ─────────────────────────────────────────────── */}
      {hasData ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

          {/* Entry */}
          {execution.entry && (
            <>
              <SectionDivider label="Entry" />
              <PlanRow label="Trigger" value={execution.entry} color="var(--gold)" size={13} />
            </>
          )}

          {/* Risk */}
          {execution.invalidation && (
            <>
              <SectionDivider label="Risk" />
              <PlanRow label="Invalidation" value={execution.invalidation} color="#f0534e" size={13} accent />
            </>
          )}

          {/* Targets — ladder */}
          {(execution.target1 || execution.target2) && (
            <>
              <SectionDivider label="Targets" />
              <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: 0 }}>
                <TargetRow label="T1" value={execution.target1} color="#2ec4a9" isFirst />
                <TargetRow label="T2" value={execution.target2} color="#5eead4" />
              </div>
            </>
          )}

          {/* R/R */}
          {execution.rr && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginTop: 8, paddingTop: 8, borderTop: '1px solid #151d2a',
            }}>
              <span style={{ fontSize: 9, color: '#2e3548' }}>Risk / Reward</span>
              <span style={{ fontSize: 14, fontWeight: 800, color: '#a78bfa' }}>{execution.rr}</span>
            </div>
          )}
        </div>
      ) : (
        <div style={{ fontSize: 10, color: '#2e3548', padding: '10px 0', lineHeight: 1.5 }}>
          {vm.stateTransition.actionability === 'avoid'
            ? 'Stand aside — no actionable setup.'
            : 'Waiting for setup confirmation.'}
        </div>
      )}

      {/* ── Management / checklist ───────────────────────────────────────── */}
      {execution.checklist.length > 0 && (
        <>
          <SectionDivider label="Manage" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {execution.checklist.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <span style={{ color: '#2e3548', fontSize: 9, marginTop: 1, flexShrink: 0 }}>›</span>
                <span style={{ fontSize: 10, color: '#4a5568', lineHeight: 1.45 }}>{item}</span>
              </div>
            ))}
          </div>
        </>
      )}

      <div style={{ flex: 1 }} />

      {/* ── Actions ──────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', gap: 6, paddingTop: 10,
        borderTop: '1px solid #151d2a', marginTop: 10,
      }}>
        <button
          onClick={onSaveSnap}
          disabled={snapSaving || snapSaved}
          style={{
            flex: 1, padding: '6px 0', borderRadius: 4,
            background: snapSaved ? 'rgba(46,196,169,0.1)' : 'rgba(201,168,76,0.08)',
            border: `1px solid ${snapSaved ? 'rgba(46,196,169,0.35)' : 'rgba(201,168,76,0.3)'}`,
            color: snapSaved ? '#2ec4a9' : 'var(--gold)',
            fontSize: 10, fontWeight: 700, fontFamily: 'var(--mono)',
            cursor: snapSaving || snapSaved ? 'default' : 'pointer', letterSpacing: .5,
          }}
        >
          {snapSaved ? '✓ SAVED' : snapSaving ? '…' : 'SAVE SNAP'}
        </button>
        <button
          onClick={onPass}
          style={{
            flex: 1, padding: '6px 0', borderRadius: 4,
            background: 'transparent', border: '1px solid #1a2236',
            color: '#2e3548',
            fontSize: 10, fontWeight: 700, fontFamily: 'var(--mono)', cursor: 'pointer', letterSpacing: .5,
          }}
        >
          PASS
        </button>
      </div>
    </div>
  );
}

// ─── Price chart panel ────────────────────────────────────────────────────────

function PriceChartPanel({
  symbol, smcOverlays,
}: {
  symbol: string;
  smcOverlays: SMCOverlaySettings;
}) {
  const { symbolData, settings, weights, anchorPoints, addAnchor, removeAnchor } = useStore();
  const data    = symbolData[symbol];
  const candles = data?.candles ?? [];
  const ctx     = candles.length ? getSymbolContext(symbol, data, settings, weights) : null;
  const zones   = ctx?.zones ?? [];
  const anchors = anchorPoints[symbol] ?? [];

  const vwap    = candles.length
    ? (() => { const a = calcVWAP(candles); return a[a.length - 1]; })()
    : null;
  const cur     = candles.length ? candles[candles.length - 1].close : null;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      background: '#131722',          /* matches chart canvas — no card feel */
      border: '1px solid #1e2230',    /* very subtle, chart-tone border */
      borderRadius: 6,
      overflow: 'hidden',
      minHeight: 300,
    }}>
      {/* Metadata strip — light overlay, not a header block */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '3px 8px',           /* reduced: was 6px 10px */
        background: 'transparent',    /* no bg fill — float over chart edge */
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 8, fontWeight: 600, color: '#4a5568', fontFamily: 'var(--mono)', letterSpacing: 1, opacity: 0.75 }}>
          {symbol}
        </span>
        {cur !== null && (
          <span style={{ fontSize: 12, fontWeight: 700, color: '#b2b5be', fontFamily: 'var(--mono)' }}>
            {cur.toFixed(2)}
          </span>
        )}
        {vwap !== null && cur !== null && (
          <span style={{
            fontSize: 9, fontFamily: 'var(--mono)', opacity: 0.65,
            color: cur >= vwap ? '#26a69a' : '#ef5350',
          }}>
            {cur >= vwap ? '▲' : '▼'} {vwap.toFixed(2)}
          </span>
        )}
        <div style={{ marginLeft: 'auto' }}>
          {/* Phase 3 commit 3.2.1 hotfix: changed wrapping <span> to <div>.
              The original code wrapped the strip inside the metadata-strip's
              <span> with fontSize: 8 inherited — invalid HTML (block inside
              inline) caused click events to be eaten by the malformed DOM,
              and the 8px font-size inheritance shrank pills below intended.
              Dropping the font-size lets the strip's own CSS govern.
              See PHASE_3_TIMEFRAME_DESIGN.md and Session 3 transcripts. */}
          <ChartTimeframeStrip symbol={symbol} />
        </div>
      </div>

      {/* Chart */}
      {candles.length ? (
        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          <CandleChart
            candles={candles}
            zones={zones}
            anchorPoints={anchors}
            onAddAnchor={ts => addAnchor(symbol, ts)}
            onRemoveAnchor={ts => removeAnchor(symbol, ts)}
          />
        </div>
      ) : (
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--blue-dim)', fontFamily: 'var(--mono)', fontSize: 11,
        }}>
          No price data — fetch or load a sample
        </div>
      )}
    </div>
  );
}

// ─── Execution Workspace ──────────────────────────────────────────────────────

interface WorkspaceProps {
  vm:          HedgeFundModeViewModel;
  symbol:      string;
  smcOverlays: SMCOverlaySettings;
  onSaveSnap:  () => Promise<void>;
  onPass:      () => void;
}

export function ExecutionWorkspace({
  vm, symbol, smcOverlays, onSaveSnap, onPass,
}: WorkspaceProps) {
  const [snapSaving, setSnapSaving] = useState(false);
  const [snapSaved,  setSnapSaved]  = useState(false);

  const handleSave = useCallback(async () => {
    setSnapSaving(true);
    try {
      await onSaveSnap();
      setSnapSaved(true);
      setTimeout(() => setSnapSaved(false), 4000);
    } finally {
      setSnapSaving(false);
    }
  }, [onSaveSnap]);

  return (
    <div className="hf-exec-workspace-row">
      {/* Left: live price chart */}
      <PriceChartPanel symbol={symbol} smcOverlays={smcOverlays} />

      {/* Right: execution plan */}
      <ExecutionPlanCard
        vm={vm}
        onSaveSnap={handleSave}
        onPass={onPass}
        snapSaving={snapSaving}
        snapSaved={snapSaved}
      />
    </div>
  );
}
